import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SkedIcon } from "ui";
import isEqual from "lodash/isEqual";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import Disclaimer from "components/MediaLibrary/SourceMedia/Disclaimer/Disclaimer";
import {
  selectExistingHashtag,
  selectHashtagFeedView,
} from "actions/sourceMedia";
import {
  getHashtagFeedView,
  getSelectedHashtag,
  getSelectedIgAccount,
} from "selectors/hashtagFeedSelectors";
import {
  HASHTAG_FEED_VIEWS,
  DEFAULT_HASHTAG_FILTER,
} from "constants/MediaLibrary";
import { MediaFilesFilters } from "@/entities/media-files";
import { getAccountsForPlatform } from "selectors/skedCoreSelectors";
import { useMediaFilesFilters } from "@/entities/media-files";
import { MediaFilesSelectedFilters } from "@/entities/media-files/componets/filters/media-files-filters";

interface HashtagFeedActionsProps {
  filterState: any;
  setFilterState: (state: any) => void;
}

interface Account {
  _id: string;
  login: string;
  [key: string]: any;
}

interface SortOption {
  value: string;
  label: string;
}

const HashtagFeedActions: React.FC<HashtagFeedActionsProps> = ({
  filterState,
  setFilterState,
}) => {
  const dispatch = useDispatch();
  const selectedHashtag = useSelector(getSelectedHashtag) as any;
  const selectedIgAccount = useSelector(getSelectedIgAccount) as any;
  const hashtagFeedView = useSelector(getHashtagFeedView) as string;

  const accounts = useSelector((state: any) =>
    getAccountsForPlatform(state, "IG")
  ) as Account[];

  const refreshMedia = (filterState: any) => {
    dispatch(
      selectExistingHashtag(
        selectedIgAccount._id,
        selectedHashtag.hashtag_id,
        filterState
      )
    );
  };

  const filterOnChange = (value: any) => {
    // NOTE: (Andrey) modify login to _id
    const newFilterState = {
      ...value,
      excludeAccounts: accounts
        .filter((account) => value.excludeAccounts.includes(account.login))
        .map((account) => account._id),
    };

    // NOTE: (Andrey) after rerender filter if it has value fired onChange, for avoid this check if old and new value is equal
    if (isEqual(filterState, newFilterState)) return;

    setFilterState(newFilterState);
    refreshMedia(newFilterState);
  };

  // NOTE: (Andrey) modify _id to login
  const filterValues = {
    ...filterState,
    excludeAccounts: accounts
      .filter((account) => filterState.excludeAccounts.includes(account._id))
      .map((account) => account.login),
  };

  const [selectedFeedView, setSelectedFeedView] = useState(hashtagFeedView);
  const [filterProps] = useMediaFilesFilters({
    values: filterValues,
    onChange: filterOnChange,
    onClear: () => filterOnChange(DEFAULT_HASHTAG_FILTER),
    tags: accounts.map((account) => account.login),
  });

  const sortOptions: SortOption[] = [
    {
      value: HASHTAG_FEED_VIEWS.RECENT,
      label: "Most recent",
    },
    {
      value: HASHTAG_FEED_VIEWS.TOP,
      label: "Top media",
    },
  ];

  const selectedOption = sortOptions.find(
    (opt) => opt.value === selectedFeedView
  );

  const handleFeedChange = (value: string) => {
    setSelectedFeedView(value);
    dispatch(selectHashtagFeedView(value, filterState));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <MediaFilesFilters
            {...filterProps}
            availableFilters={["excludeAccounts"]}
          />
          <MediaFilesSelectedFilters {...filterProps} />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {/* Sort dropdown */}
          <div data-testid="selectHashtagView">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-w-[160px] justify-between"
                >
                  <span className="text-sm">
                    {selectedOption?.label || "Sort by"}
                  </span>
                  <i className="fa-solid fa-chevron-down text-xs text-neutral-400 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[160px]">
                {sortOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    onClick={() => handleFeedChange(option.value)}
                    className={cn(
                      selectedFeedView === option.value &&
                        "bg-blue-50 text-blue-700 font-medium"
                    )}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Refresh button */}
          <button
            onClick={() => refreshMedia(filterState)}
            className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-neutral-50 transition-colors"
          >
            <SkedIcon icon="icon-refresh" size="18" />
          </button>
        </div>
      </div>
      <div className="mb-4">
        <Disclaimer />
      </div>
    </div>
  );
};

export default HashtagFeedActions;
